import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-hot-toast";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { HiOutlineTrash, HiOutlineChevronRight, HiOutlineChevronUp, HiOutlineChevronDown, HiOutlineX, HiOutlineDotsVertical, HiOutlinePlus, HiOutlineExclamation, HiOutlineSparkles, HiOutlineRefresh } from "react-icons/hi";
import { PiClockCounterClockwise } from "react-icons/pi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import API from "../../api";
import { capture } from "../../instrument";
import { INPUT_CLASS_NAME, TUBE_TYPE, TUBE_TYPE_LABELS, TUBE_TYPE_COLORS, needsVerification, CONFIG_STATUS, CONFIG_STATUS_LABEL } from "../../utils";
import { useHotkeys } from "react-hotkeys-hook";
import useRecordNavShortcuts from "../../hooks/useRecordNavShortcuts";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";
import SyncInfoBanner from "../../components/SyncInfoBanner";
import SyncConflictBanner from "../../components/SyncConflictBanner";
import DependencyExplorer from "../../components/dependencies/DependencyExplorer";
import CodeLookup from "../../components/CodeLookup";

const EditCode = () => {
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const isDirtyRef = useRef(false);
  const markDirty = () => {
    isDirtyRef.current = true;
  };

  const filteredIds = location.state?.filteredIds || null;
  const currentIndex = useMemo(() => (filteredIds ? filteredIds.indexOf(id) : -1), [filteredIds, id]);
  const hasNavigation = filteredIds !== null && currentIndex !== -1;

  const preview = useMemo(() => {
    const cached = queryClient
      .getQueriesData({ queryKey: ["codes"] })
      .flatMap(([, codes]) => codes || [])
      .find((c) => c._id === id);
    return cached ? { value: cached.value, type: cached.type, name: cached.name } : null;
  }, [id, queryClient]);

  const navigateTo = (index) => {
    const targetId = filteredIds[index];
    history.replace({ pathname: `/code/${targetId}/edit`, state: { filteredIds } });
  };

  const navigatePrev = () => {
    if (!hasNavigation || currentIndex <= 0) return;
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    navigateTo(currentIndex - 1);
  };

  const navigateNext = () => {
    if (!hasNavigation || currentIndex >= filteredIds.length - 1) return;
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    navigateTo(currentIndex + 1);
  };

  const goBack = () => {
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    history.push("/code");
  };

  useRecordNavShortcuts(
    {
      onPrev: navigatePrev,
      onNext: navigateNext,
      onBack: goBack,
      canPrev: hasNavigation && currentIndex > 0,
      canNext: hasNavigation && currentIndex < filteredIds?.length - 1,
    },
    [hasNavigation, currentIndex, filteredIds],
  );

  const [value, setValue] = useState("");
  const [name, setName] = useState("");
  const [codeType, setCodeType] = useState("");
  const [type, setType] = useState("DEFAULT");
  const [sampleId, setSampleId] = useState("");

  const [UMLS, setUMLS] = useState("");
  const [ubilabUrl, setUbilabUrl] = useState("");
  const [relatedCodes, setRelatedCodes] = useState([]);
  const [incompatibleCodes, setIncompatibleCodes] = useState([]);
  const [enabledAtFrontDesk, setEnabledAtFrontDesk] = useState(true);
  const [tubes, setTubes] = useState([]);

  const currentCodes = useMemo(() => (value ? [value] : []), [value]);

  const {
    data: codeData,
    isLoading: isLoadingData,
    isError,
  } = useQuery({
    queryKey: ["code", id],
    queryFn: async () => {
      const response = await API.get(`/code/${id}`);
      if (!response.ok) throw new Error(response.message || "Impossible de charger le code");
      return response.data;
    },
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!codeData) return;
    setValue(codeData.value || "");
    setName(codeData.name || "");
    setCodeType(codeData.codeType || "");
    setType(codeData.type || "DEFAULT");
    setSampleId(codeData.sampleId || "");
    setUMLS(codeData.UMLS || "");
    setUbilabUrl(codeData.ubilabUrl || "");
    setRelatedCodes(codeData.relatedCodes || []);
    setIncompatibleCodes(codeData.incompatibleCodes || []);
    setEnabledAtFrontDesk(codeData.enabledAtFrontDesk !== false);
    setTubes(codeData.tubes || []);
    isDirtyRef.current = false;
  }, [codeData]);

  useEffect(() => {
    if (!isError) return;
    toast.error("Erreur lors du chargement du code");
    history.push("/code");
  }, [isError]);

  const referencingTables = codeData?.referencingTables || [];

  const { data: diagramData } = useQuery({
    queryKey: ["diagramData"],
    queryFn: async () => {
      const [questions, guidelines, actions] = await Promise.all([API.get("/question"), API.get("/guideline"), API.get("/action")]);
      if (!questions.ok || !guidelines.ok || !actions.ok) throw new Error("Impossible de charger les dépendances");
      return { questions: questions.data, guidelines: guidelines.data, actions: actions.data };
    },
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateMutation = useMutation({
    mutationFn: (body) => API.put(`/code/${id}`, body),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      isDirtyRef.current = false;
      toast.success("Code mis à jour avec succès");
      queryClient.invalidateQueries({ queryKey: ["codes"] });
      queryClient.invalidateQueries({ queryKey: ["code", id] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors de la mise à jour du code");
    },
  });

  const { data: syncOverrides = [] } = useQuery({
    queryKey: ["sync-overrides", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const response = await API.get(`/sync/override?docId=${id}`);
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
  });
  const hasConflict = syncOverrides.some((override) => override.conflict);

  const verifyMutation = useMutation({
    mutationFn: () => API.post("/code/bulk", { ids: [id], action: "verify" }),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Code vérifié");
      queryClient.invalidateQueries({ queryKey: ["codes"] });
      queryClient.invalidateQueries({ queryKey: ["code", id] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => API.put(`/code/${id}`, { isDeleted: false }),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Code restauré");
      queryClient.invalidateQueries({ queryKey: ["codes"] });
      queryClient.invalidateQueries({ queryKey: ["code", id] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => API.delete(`/code/${id}`),
    onSuccess: (response) => {
      if (!response.ok) {
        if (response.code === "CODE_IN_USE") return toast.error("Impossible de supprimer un code référencé par des tables");
        return toast.error(response.message);
      }
      toast.success("Code supprimé avec succès");
      queryClient.invalidateQueries({ queryKey: ["codes"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
      history.push("/code");
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors de la suppression du code");
    },
  });

  const isMutating = updateMutation.isPending || deleteMutation.isPending;
  const isDeleted = !!codeData?.isDeleted;

  const updateCode = () => {
    updateMutation.mutate({
      value,
      name,
      codeType: codeType || null,
      type,
      sampleId,
      UMLS,
      ubilabUrl,
      relatedCodes: relatedCodes.map((c) => c._id).filter(Boolean),
      incompatibleCodes: incompatibleCodes.map((c) => c._id).filter(Boolean),
      enabledAtFrontDesk,
      tubes,
    });
  };

  useHotkeys(SHORTCUTS.EDIT_SAVE.keys, () => updateCode(), { preventDefault: true, enableOnFormTags: true, enabled: !isMutating && !isDeleted }, [updateCode, isMutating, isDeleted]);

  const deleteCode = () => {
    if (referencingTables.length > 0) {
      return toast.error("Impossible de supprimer un code référencé par des tables");
    }
    const confirmed = window.confirm("Voulez-vous vraiment supprimer ce code ? Cette action est irréversible.");
    if (!confirmed) return;
    deleteMutation.mutate();
  };

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      {hasNavigation && (
        <div className="flex items-center gap-1.5 border-b border-gray-200 bg-white px-3 py-1.5">
          <button type="button" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={goBack} title="Fermer (Esc)">
            <HiOutlineX className="h-4 w-4" />
          </button>
          <div className="mx-0.5 h-4 w-px bg-gray-200" />
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            onClick={navigatePrev}
            disabled={currentIndex <= 0}
          >
            <HiOutlineChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            onClick={navigateNext}
            disabled={currentIndex >= filteredIds.length - 1}
          >
            <HiOutlineChevronDown className="h-4 w-4" />
          </button>
          <span className="ml-1 text-xs tabular-nums text-gray-500">
            {currentIndex + 1} / {filteredIds.length}
          </span>
        </div>
      )}

      {isDeleted && (
        <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-100 px-8 py-2.5">
          <HiOutlineTrash className="h-4 w-4 shrink-0 text-gray-500" />
          <p className="text-sm text-gray-600">Ce code est supprimé : il est affiché en lecture seule. Restaurez-le pour pouvoir le modifier.</p>
        </div>
      )}

      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="truncate text-lg font-semibold text-gray-900">{codeData?.value || preview?.value || "Code"}</h1>
            {(codeData?.type || preview?.type) && (
              <span className="shrink-0 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{codeData?.type || preview?.type}</span>
            )}
            {codeData && needsVerification(codeData) && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-orange-200 bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700">
                {codeData.configStatus === CONFIG_STATUS.CREATED ? <HiOutlineSparkles className="h-3 w-3" /> : <HiOutlineRefresh className="h-3 w-3" />}
                {CONFIG_STATUS_LABEL[codeData.configStatus]}
              </span>
            )}
            {hasConflict && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                <HiOutlineExclamation className="h-3 w-3" />
                Conflit
              </span>
            )}
            {isDeleted && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                <HiOutlineTrash className="h-3 w-3" />
                Supprimé
              </span>
            )}
            {!isDeleted && codeData?._sources?.some((s) => s.missingFromSource) && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                <HiOutlineTrash className="h-3 w-3" />
                Supprimé
              </span>
            )}
          </div>
          {(codeData?.name || preview?.name) && <p className="mt-1 truncate text-[13px] text-gray-500">{codeData?.name || preview?.name}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <div className="relative" ref={menuRef}>
            <button type="button" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <HiOutlineDotsVertical className="h-5 w-5" />
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 z-10 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-t-lg px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={() => {
                    setIsMenuOpen(false);
                    window.open(`/audit-log/code2/${id}`, "_blank", "noopener,noreferrer,width=1000,height=800");
                  }}
                >
                  <PiClockCounterClockwise className="h-4 w-4" />
                  Historique
                </button>
                {!isDeleted && (
                  <button
                    type="button"
                    className={`flex w-full items-center gap-2 rounded-b-lg px-3 py-2 text-sm transition-colors ${
                      referencingTables.length > 0 ? "cursor-not-allowed text-gray-400" : "text-red-600 hover:bg-red-50"
                    }`}
                    onClick={() => {
                      setIsMenuOpen(false);
                      deleteCode();
                    }}
                    disabled={isMutating || referencingTables.length > 0}
                    title={referencingTables.length > 0 ? "Ce code est référencé par des tables" : "Supprimer le code"}
                  >
                    <HiOutlineTrash className="h-4 w-4" />
                    Supprimer
                  </button>
                )}
              </div>
            )}
          </div>
          {!isDeleted && codeData && needsVerification(codeData) && (
            <button
              type="button"
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
              onClick={() => {
                if (isDirtyRef.current) return toast.error("Enregistrez vos modifications avant de vérifier");
                verifyMutation.mutate();
              }}
              disabled={verifyMutation.isPending}
            >
              {verifyMutation.isPending ? "Vérification..." : "Marquer vérifié"}
            </button>
          )}
          {isDeleted ? (
            <button
              type="button"
              className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
              onClick={() => restoreMutation.mutate()}
              disabled={restoreMutation.isPending}
            >
              {restoreMutation.isPending ? "Restauration..." : "Restaurer"}
            </button>
          ) : (
            <button
              type="button"
              className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
              onClick={() => {
                updateCode();
                notifyShortcut("EDIT_SAVE");
              }}
              disabled={isMutating || isLoadingData}
            >
              {updateMutation.isPending ? "Enregistrement..." : "Enregistrer"}
            </button>
          )}
        </div>
      </div>

      {isLoadingData ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
          <span className="text-sm text-gray-400">Chargement...</span>
        </div>
      ) : (
        <div className="grid flex-1 grid-cols-5 overflow-hidden">
          <div className={`col-span-3 flex-1 overflow-y-auto ${isDeleted ? "pointer-events-none opacity-60" : ""}`} onChangeCapture={markDirty}>
            <fieldset disabled={isDeleted} className="mx-auto max-w-4xl px-8 py-6">
              <SyncConflictBanner docId={id} />
              <h3 className="mb-4 text-lg font-medium text-gray-900">Détails</h3>

              <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Nom</label>
                  <input className={INPUT_CLASS_NAME} value={name} onChange={(e) => setName(e.target.value)} disabled={isMutating} placeholder="Numération Formule Sanguine" />
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Valeur</label>
                  <input className={INPUT_CLASS_NAME} value={value} onChange={(e) => setValue(e.target.value)} disabled={isMutating} placeholder="NFS" />
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Type de code</label>
                  <select className={INPUT_CLASS_NAME} value={codeType} onChange={(e) => setCodeType(e.target.value)}>
                    <option value="">—</option>
                    <option value="groupe">Groupe</option>
                    <option value="analyse">Analyse</option>
                  </select>
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Type</label>
                  <select className={INPUT_CLASS_NAME} value={type} onChange={(e) => setType(e.target.value)}>
                    <option value="DEFAULT">DEFAULT</option>
                    <option value="SANG">SANG</option>
                    <option value="ECHANTILLON">ECHANTILLON</option>
                    <option value="H24">H24</option>
                    <option value="SEROLOGIE">SEROLOGIE</option>
                    <option value="PCR">PCR</option>
                    <option value="SELLE">SELLE</option>
                    <option value="AUTRE">AUTRE</option>
                  </select>
                </div>
                {codeData && codeData.type !== type && referencingTables.length > 0 && (
                  <div className="px-5 pb-3.5">
                    <div className="flex rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <HiOutlineExclamation className="mr-3 mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium">
                          Ce code passera de {codeData.type} à {type} dans {referencingTables.length} table{referencingTables.length > 1 ? "s" : ""} de transcodage.
                        </p>
                        <p className="mt-1">{`Les mots-clés de ces tables ne suivront pas : ils resteront en ${codeData.type} et le mapping deviendra incohérent tant qu'ils n'auront pas été déplacés.`}</p>
                        <p className="mt-2">{referencingTables.map((table) => table.codes?.map((c) => c.value).join(", ") || table._id).join(" · ")}</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Code échantillon</label>
                  <input className={INPUT_CLASS_NAME} value={sampleId} onChange={(e) => setSampleId(e.target.value)} placeholder="Code échantillon" />
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">UMLS</label>
                  <input className={INPUT_CLASS_NAME} value={UMLS} onChange={(e) => setUMLS(e.target.value)} placeholder="UMLS" />
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">URL Ubilab</label>
                  <input className={INPUT_CLASS_NAME} value={ubilabUrl} onChange={(e) => setUbilabUrl(e.target.value)} placeholder="https://ubilab.io/..." />
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Sous-codes associés</label>
                  <div className="flex-1">
                    <CodeLookup initialValues={relatedCodes} onChange={(codes) => { markDirty(); setRelatedCodes(codes); }} placeholder="Rechercher un code..." allowManualEntry={false} />
                  </div>
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Codes incompatibles</label>
                  <div className="flex-1">
                    <CodeLookup
                      initialValues={incompatibleCodes}
                      onChange={(codes) => {
                        markDirty();
                        setIncompatibleCodes(codes);
                      }}
                      placeholder="Rechercher un code..."
                      allowManualEntry={false}
                    />
                  </div>
                </div>
                <div className="flex items-start gap-4 px-5 py-3.5">
                  <label className="w-44 shrink-0 pt-1.5 text-[13px] font-medium text-gray-600">Tubes à prélever</label>
                  <div className="flex flex-1 flex-col gap-2">
                    {tubes.map((tube, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          className={`${INPUT_CLASS_NAME} !w-16 !flex-none text-center`}
                          value={tube.quantity}
                          onChange={(e) => {
                            markDirty();
                            const updated = [...tubes];
                            updated[index] = { ...updated[index], quantity: Math.max(1, parseInt(e.target.value) || 1) };
                            setTubes(updated);
                          }}
                        />
                        <div className="relative flex flex-1 items-center">
                          <span className={`pointer-events-none absolute left-2.5 h-2 w-2 rounded-full ${TUBE_TYPE_COLORS[tube.type]?.dot || "bg-gray-400"}`} />
                          <select
                            className={`${INPUT_CLASS_NAME} pl-7`}
                            value={tube.type}
                            onChange={(e) => {
                              markDirty();
                              const updated = [...tubes];
                              updated[index] = { ...updated[index], type: e.target.value };
                              setTubes(updated);
                            }}
                          >
                            {Object.values(TUBE_TYPE).map((tubeType) => (
                              <option key={tubeType} value={tubeType}>
                                {TUBE_TYPE_LABELS[tubeType]}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                          onClick={() => {
                            markDirty();
                            setTubes(tubes.filter((_, i) => i !== index));
                          }}
                        >
                          <HiOutlineTrash className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="flex items-center gap-1 text-sm text-gray-500 transition-colors hover:text-primary-500"
                      onClick={() => {
                        markDirty();
                        setTubes([...tubes, { quantity: 1, type: TUBE_TYPE.EDTA }]);
                      }}
                    >
                      <HiOutlinePlus className="h-4 w-4" />
                      Ajouter un tube
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-4 px-5 py-3.5">
                  <div className="w-44 shrink-0">
                    <span className="text-[13px] font-medium text-gray-600">Disponible à la saisie</span>
                    <span className="block text-xs text-gray-500">Autocomplete de l'accueil</span>
                  </div>
                  <button
                    type="button"
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${enabledAtFrontDesk ? "bg-primary-500" : "bg-gray-300"}`}
                    onClick={() => {
                      markDirty();
                      setEnabledAtFrontDesk(!enabledAtFrontDesk);
                    }}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enabledAtFrontDesk ? "translate-x-5" : "translate-x-0"}`}
                    />
                  </button>
                </div>
              </div>

              {referencingTables.length > 0 && (
                <div className="mt-8">
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-medium text-gray-900">
                    Tables référentes
                    <span className="rounded-full bg-gray-200 px-1.5 py-0.5 text-xs font-medium text-gray-500">{referencingTables.length}</span>
                  </h3>
                  <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-100 bg-white">
                    <div className="max-h-60 divide-y divide-gray-100 overflow-y-auto">
                      {referencingTables.map((table) => {
                        const tableLabel = table.codes?.map((c) => c.value).join(", ") || table._id;
                        return (
                          <div
                            key={table._id}
                            className="flex cursor-pointer items-center px-5 py-3 transition-colors hover:bg-gray-50"
                            onClick={() => history.push(`/table/${table._id}/edit`)}
                          >
                            <span className="min-w-0 truncate text-sm text-gray-900">{tableLabel}</span>
                            <HiOutlineChevronRight className="ml-auto h-4 w-4 shrink-0 text-gray-300" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </fieldset>
          </div>

          <div className="col-span-2 shrink-0 overflow-y-auto border-l border-gray-200 bg-white p-6">
            {codeData?._sources?.length > 0 && (
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-medium text-gray-900">Synchronisation</h3>
                <SyncInfoBanner sources={codeData?._sources} docId={id} entityType="code" lastSyncChangedFields={codeData?.lastSyncChangedFields} lastSyncChangedAt={codeData?.lastSyncChangedAt} />
              </div>
            )}
            <h3 className="mb-4 text-lg font-medium text-gray-900">Dépendances</h3>
            {diagramData ? (
              <DependencyExplorer data={diagramData} currentCodes={currentCodes} className="rounded-lg border border-gray-100" />
            ) : (
              <div className="flex items-center justify-center rounded-lg border border-gray-100 py-8 text-sm text-gray-400">Chargement du diagramme...</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EditCode;
